import * as React from 'react';
import styled from 'styled-components';
import { tablet } from '../../../../BreakPoints';
import { Divider } from '../../../../Divider';
import { UserPanel } from './UserPanel';
import { MenuItem, MenuItemGroup, MenuItemProps, MenuItemGroupProps } from './MenuItem';
import { Flex } from '../../../../compositeComponents/FlexHelpers';
import { Icon } from '../../../../icons/Icons';
import { ProtectedPageContext } from '../../../../../pages/_contexts/ProtectedPageContext';
import { UserToken } from '../../../../../amorg-common';
import { useEffect } from 'react';
import { Strong } from '../../../../typography/Body';



type MenuItemDefinition = 
    MenuItemProps & {
        type:'menu-item',
        show:boolean
    };

type MenuItemGroupDefinition =
    MenuItemGroupProps & {
        type:'menu-item-group',
        subItems: MenuItemDefinition[]
    };

type MenuItemDividerDefinition = {
    type:'menu-divider'
}

type NavigationItemDefinition = MenuItemDefinition | MenuItemGroupDefinition | MenuItemDividerDefinition;

type LeftMenuNavigationProps = {
    activeUser: UserToken | null,
    selectedMenuItemKey?: string,
    items: NavigationItemDefinition[],
    loading?:boolean,
    hideMenu?:boolean,
};

export const LeftMenuNavigation = ({ activeUser, selectedMenuItemKey, items, loading, hideMenu }: LeftMenuNavigationProps): JSX.Element => 
{
    const [isMobileNavigationActive, setIsMobileNavigationActive] = React.useState(false);
    const context = React.useContext(ProtectedPageContext);

    useEffect(() => {
        // Handle scroll lock for mobile navigation
        if (isMobileNavigationActive) {
            document.body.classList.add('no-scroll');
            document.documentElement.style.overflowX = 'hidden';
            document.body.style.overflowX = 'hidden';
        } else {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.overflowX = '';
            document.body.style.overflowX = '';
        }

        return () => {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.overflowX = '';
            document.body.style.overflowX = '';
        };
    }, [isMobileNavigationActive]);

    const handleMenuToggle = () =>
    {
        setIsMobileNavigationActive(!isMobileNavigationActive);
    };

    const applyPartnerLogo = () =>
    {

        if (context.user.partner === 'Crecea')
        {
            return <img src="/images/partner/crecea/crecea_logo_white.svg" />;
        }

        if(context.user.partner === 'Joblife')
        {
            return <img width="200" src="/images/partner/creceajoblife/CreceaJoblife_white.svg" />;
        }

    };

    return (
        <OuterWrapper isMobileNavigationActive={isMobileNavigationActive}>

            <MenuWrapper className='MenuWrapper'>
                <Flex flexDirection="row" alignItems='center' justifyContent='space-between'>

                    {!hideMenu &&
                        <NavigationTrigger onClick={handleMenuToggle}>
                            <Icon name="navigation_menu" size="medium" />
                        </NavigationTrigger>
                    }
                </Flex>
                {!hideMenu &&
                    <>
                        <MenuContent isMobileNavigationActive={isMobileNavigationActive}>
                            {applyPartnerLogo()}
                            <Navigation className="_navigation">
                                <List>
                                    <>
                                        {!loading && activeUser &&
                                        <>
                                            {items.map((item, index) => {
                                                if(item.type === 'menu-item')
                                                {
                                                    if(item.show)
                                                    {
                                                        return <MenuItem key={index} selectedMenuItemKey={selectedMenuItemKey} {...item} />
                                                    }
                                                    else {
                                                        return null;
                                                    }
                                                }
                                                else if(item.type === 'menu-item-group')
                                                {
                                                    if(item.subItems.some(subItem => subItem.show))
                                                    {
                                                        return (
                                                            <MenuItemGroup key={index} {...item}>
                                                                {item.subItems.map((subItem, subIndex) => {
                                                                    if(subItem.type === 'menu-item' && subItem.show)
                                                                    {
                                                                        return <MenuItem key={subIndex} selectedMenuItemKey={selectedMenuItemKey} {...subItem} />
                                                                    }
                                                                    return null;
                                                                })}
                                                            </MenuItemGroup>
                                                        )
                                                    }
                                                }
                                                else if(item.type === 'menu-divider')
                                                {
                                                    const hasItemsBefore = index > 0 
                                                        && items.slice(0, index).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    const hasItemsAfter = index < items.length - 1 
                                                        && items.slice(index + 1).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    if(hasItemsBefore && hasItemsAfter)
                                                    {
                                                        return <Divider key={index} color={context.colors.mainNavigation.divider} />
                                                    }
                                                }
                                            })}
                                        </>
                                    }
                                </>
                            </List>
                        </Navigation>
                        </MenuContent>
                    </>
                }
            </MenuWrapper>
            <MenuFooter isMobileNavigationActive={isMobileNavigationActive}>
                {!loading &&
                    <UserPanel activeUser={activeUser} />
                }
                <Flex flexDirection="row" alignItems='center' marginTop={8}>
                    {(context.user.partner) &&
                        <PoweredBy>Powered by</PoweredBy>
                    }
                    <LogoContainer>
                        <img src="/images/amorg_logo_blue.svg" />
                    </LogoContainer>
                    <Strong>AMORG</Strong>
                </Flex>
            </MenuFooter>
        </OuterWrapper>
    );
};



const NavigationTrigger = styled.div`

    display:none;

    @media (max-width: ${tablet}px) {
        display:block;

        & svg{
            fill: white;
            
        }
    }

`;

const OuterWrapper = styled.div<{ isMobileNavigationActive: boolean; }>`  
    font-family: "Noto Sans", sans-serif;
    color: white;
    background-color: ${props => props.theme.colors.mainNavigation.background};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    overflow-y: auto;
    
    /* Desktop styles */
    width: 320px;
    height: 100%;
    padding: 24px 24px 10px 24px;
    position: fixed;

    @media (max-width: ${tablet}px) {
        /* Mobile styles */
        width: 100%;
        max-width: 100vw;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        padding: 16px 24px 10px 24px;
        overflow-x: hidden;
        overflow-y: auto;
        height: ${props => props.isMobileNavigationActive ? '100vh' : '70px'};
        z-index: ${props => props.isMobileNavigationActive ? '1000' : '10'};
    }
`;

const MenuWrapper = styled.div``;

const MenuFooter = styled.div<{ isMobileNavigationActive: boolean }>`
    @media (max-width: ${tablet}px) {
        display: ${props => props.isMobileNavigationActive ? 'block' : 'none'};
    }
`;

const MenuContent = styled.div<{ isMobileNavigationActive: boolean }>`
    @media (max-width: ${tablet}px) {
        display: ${props => props.isMobileNavigationActive ? 'block' : 'none'};
    }
`;

const LogoContainer = styled.div`
    height: 32px;
    width: 32px;
    margin-left: 8px;
    margin-right: 8px;
`;

const Navigation = styled.nav`
    margin:12px auto;   
`;

const List = styled.ul`
    list-style: none;
    margin:0px;
    padding:0px;
`;

const PoweredBy = styled.div`
    font-size: 12px;
    color: white;
    font-weight: 700;
`;